import { useEffect, useSyncExternalStore } from 'react'

/**
 * Roster roles and squad types — Settings → Kadro Rolleri / Takım Türleri.
 *
 * One document from `/api/roster-catalog`, shared by the roster builder, the
 * roster view in match details and Settings. Held in a module store so a save
 * in Settings reaches every open screen without a reload.
 */

export interface RosterRole { key: string; name: string; emoji: string }

export interface SquadRule {
  role: string
  min: number
  max: number
  /** Slots of this role a freshly added squad of the type gets. */
  default: number
}

export interface SquadType { key: string; name: string; emoji: string; rules: SquadRule[] }

export interface RosterCatalog { roles: RosterRole[]; squad_types: SquadType[] }

const EMPTY: RosterCatalog = { roles: [], squad_types: [] }

let current: RosterCatalog = EMPTY
let loaded = false
let inflight: Promise<void> | null = null
const listeners = new Set<() => void>()

function emit() { listeners.forEach(l => l()) }

export function setRosterCatalog(next: RosterCatalog) {
  current = next
  loaded = true
  emit()
}

function load() {
  if (loaded || inflight) return
  inflight = fetch('/api/roster-catalog', { credentials: 'include' })
    .then(r => (r.ok ? r.json() : null))
    .then((d: RosterCatalog | null) => { if (d) setRosterCatalog(d) })
    .catch(() => {})
    .finally(() => { inflight = null })
}

/** The catalog; empty until the first fetch lands. */
export function useRosterCatalog(): RosterCatalog {
  useEffect(load, [])
  return useSyncExternalStore(
    cb => { listeners.add(cb); return () => { listeners.delete(cb) } },
    () => current,
  )
}

// ── Lookups ───────────────────────────────────────────────────────────────────

export function roleOf(catalog: RosterCatalog, key: string): RosterRole | undefined {
  return catalog.roles.find(r => r.key === key)
}

export function typeOf(catalog: RosterCatalog, key: string): SquadType | undefined {
  return catalog.squad_types.find(t => t.key === key)
}

/** A new squad's slots: each rule's default count, in rule order. */
export function defaultSlots(type: SquadType): string[] {
  return type.rules.flatMap(rule => Array(rule.default).fill(rule.role))
}

/** Slots regrouped in rule order after a count changes. */
export function orderedSlots(type: SquadType | undefined, counts: Record<string, number>): string[] {
  return (type?.rules ?? []).flatMap(rule => Array(Math.max(0, counts[rule.role] ?? 0)).fill(rule.role))
}
